import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader, AdminEmptyState } from "@/components/admin/crud-helpers";
import { Button } from "@/components/ui/button";
import { Upload, Trash2, RefreshCw, Copy, FileText, ExternalLink, Image as ImageIcon, HardDrive } from "lucide-react";
import {
  uploadMedia, deleteMedia, replaceMedia, getPublicUrl,
  formatBytes, type MediaFile,
} from "@/lib/media";
import { format } from "date-fns";

export const Route = createFileRoute("/_authenticated/admin/files")({
  component: FilesAdmin,
});

function FilesAdmin() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "images" | "docs">("all");
  const uploadRef = useRef<HTMLInputElement>(null);
  const replaceRef = useRef<HTMLInputElement>(null);
  const [replaceTarget, setReplaceTarget] = useState<MediaFile | null>(null);

  const { data: files, isLoading } = useQuery({
    queryKey: ["media_files", "list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("media_files").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data as MediaFile[];
    },
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["media_files", "list"] });

  const uploadMut = useMutation({
    mutationFn: async (list: FileList) => {
      for (const f of Array.from(list)) await uploadMedia(f);
    },
    onSuccess: () => { invalidate(); toast.success("Uploaded"); },
    onError: (e: any) => toast.error(e.message ?? "Upload failed"),
  });

  const deleteMut = useMutation({
    mutationFn: (f: MediaFile) => deleteMedia(f),
    onSuccess: () => { invalidate(); toast.success("Deleted"); },
    onError: (e: any) => toast.error(e.message ?? "Delete failed"),
  });

  const replaceMut = useMutation({
    mutationFn: ({ existing, file }: { existing: MediaFile; file: File }) => replaceMedia(existing, file),
    onSuccess: () => { invalidate(); toast.success("Replaced"); },
    onError: (e: any) => toast.error(e.message ?? "Replace failed"),
  });

  const filtered = useMemo(() => {
    return (files ?? []).filter((f) => {
      const m = f.mime_type ?? "";
      if (filter === "images" && !m.startsWith("image/")) return false;
      if (filter === "docs" && m.startsWith("image/")) return false;
      if (search && !f.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [files, search, filter]);

  const stats = useMemo(() => {
    const all = files ?? [];
    return {
      all: all.length,
      images: all.filter((f) => (f.mime_type ?? "").startsWith("image/")).length,
      docs: all.filter((f) => !(f.mime_type ?? "").startsWith("image/")).length,
      totalBytes: all.reduce((s, f) => s + (f.size ?? 0), 0),
    };
  }, [files]);

  const tabs: { id: typeof filter; label: string; icon: any; count: number }[] = [
    { id: "all", label: "All", icon: HardDrive, count: stats.all },
    { id: "images", label: "Images", icon: ImageIcon, count: stats.images },
    { id: "docs", label: "Docs", icon: FileText, count: stats.docs },
  ];

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="File manager"
        description="Upload, replace, and delete media used across pages, posts, and the site."
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search files by name…"
        count={filtered.length}
        countLabel={`of ${stats.all} · ${formatBytes(stats.totalBytes)}`}
        actions={
          <>
            <input
              ref={uploadRef}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                if (e.target.files?.length) uploadMut.mutate(e.target.files);
                e.target.value = "";
              }}
            />
            <Button size="sm" onClick={() => uploadRef.current?.click()} disabled={uploadMut.isPending}>
              <Upload className="mr-1.5 h-4 w-4" />
              {uploadMut.isPending ? "Uploading…" : "Upload"}
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap gap-1.5">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = filter === t.id;
          return (
            <Button key={t.id} size="sm" variant={active ? "default" : "outline"} onClick={() => setFilter(t.id)}>
              <Icon className="mr-1.5 h-3.5 w-3.5" /> {t.label} <span className="ml-1 opacity-70">({t.count})</span>
            </Button>
          );
        })}
      </div>

      <input
        ref={replaceRef}
        type="file"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && replaceTarget) replaceMut.mutate({ existing: replaceTarget, file });
          setReplaceTarget(null);
          e.target.value = "";
        }}
      />

      {isLoading ? (
        <p className="text-center text-muted-foreground py-12">Loading…</p>
      ) : !filtered.length ? (
        <AdminEmptyState
          title={search || filter !== "all" ? "No files match" : "No files yet"}
          description="Drop files or click Upload to add media."
          action={<Button size="sm" onClick={() => uploadRef.current?.click()}><Upload className="mr-1.5 h-4 w-4" /> Upload files</Button>}
        />
      ) : (

      <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {filtered.map((f) => {
          const url = getPublicUrl(f.path);
          const isImg = (f.mime_type ?? "").startsWith("image/");
          return (
            <div key={f.id} className="group rounded-xl border border-border bg-card overflow-hidden flex flex-col transition hover:border-primary/40 hover:shadow-glow">
              <div className="aspect-video bg-muted flex items-center justify-center overflow-hidden">
                {isImg ? (
                  <img src={url} alt={f.name} className="h-full w-full object-cover transition group-hover:scale-105" loading="lazy" />
                ) : (
                  <FileText className="h-10 w-10 text-muted-foreground" />
                )}
              </div>
              <div className="p-3 flex-1 flex flex-col gap-1.5">
                <div className="text-sm font-medium truncate" title={f.name}>{f.name}</div>
                <div className="text-xs text-muted-foreground flex items-center justify-between">
                  <span>{formatBytes(f.size)}</span>
                  <span>{format(new Date(f.created_at), "MMM d")}</span>
                </div>
                {f.width && f.height && (
                  <div className="text-[10px] text-muted-foreground">{f.width} × {f.height}</div>
                )}
                <div className="mt-auto pt-2 flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Copy URL"
                    onClick={() => { navigator.clipboard.writeText(url); toast.success("URL copied"); }}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Open" asChild>
                    <a href={url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3.5 w-3.5" /></a>
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Replace"
                    onClick={() => { setReplaceTarget(f); replaceRef.current?.click(); }}>
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive ml-auto" title="Delete"
                    onClick={() => { if (confirm(`Delete "${f.name}"?`)) deleteMut.mutate(f); }}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
